import { useEffect, useMemo, useRef, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { Link, useNavigate, useParams } from "react-router-dom";
import { deleteLead, editLead, getLead, getLeadServices, getLocations, markLeadAsRead, updateLead, updateLeadServices } from "../services/api";
import { track } from "../services/analytics";
import { useIsMobile } from "../hooks/useIsMobile";
import { hoursSinceCreated } from "../utils/leadUtils";
import { formatLeadTypeToIcon } from "../utils/displayFormatters";
import { appointmentHelp } from '../utils/helpContent';
import AboutLead from "./AboutLead";
import AppointmentForm from "./AppointmentForm";
import ContactedModal from "./ContactedModal";
import HelpTooltip from './HelpTooltip';
import LeadActRow from "./LeadActRow";
import LeadAppointmentList from "./LeadAppointmentList";
import LeadDetailSidecart from "./LeadDetailSidecart";
import LeadHeroSection from "./LeadHeroSection";
import LeadMessaging from "./LeadMessaging";
import LeadMobilePage from "./LeadMobilePage";
import LeadNavigationButtons from "./LeadNavigationButtons";
import LeadNotes from "./LeadNotes";
import NoteCreate from "./NoteCreate";
import LeadStatusControl from "./LeadStatusControl";
import LossReasonModal from "./LossReasonModal";
import NoteDetail from "./NoteDetail";
import ObjectHistory from "./ObjectHistory";
import ReasonLine from "./ReasonLine";
import ServiceSelector from "./ServiceSelector";

const LeadDetail = ({
  authToken,
  lead: propLead,
  isSideCart = false,
  onLeadUpdate,
  setTitle,
  onClose = null,
  onMessagesRead,
}) => {
  const [lead, setLead] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { id: urlId } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // State for inline editing
  const [editingField, setEditingField] = useState(null);
  const [editValue, setEditValue] = useState("");
  const [locations, setLocations] = useState([]);

  // State for note viewing
  const [selectedNoteId, setSelectedNoteId] = useState(null);

  // State for services
  const [leadServices, setLeadServices] = useState([]);
  const [servicesLoading, setServicesLoading] = useState(false);

  // State for loss reason modal
  const [isLossReasonModalOpen, setIsLossReasonModalOpen] = useState(false);
  const [pendingStatus, setPendingStatus] = useState(null);
  const [pendingCategory, setPendingCategory] = useState(null);
  // State for contacted modal
  const [isContactedModalOpen, setIsContactedModalOpen] = useState(false);

  // State for appointments
  const [showAppointmentForm, setShowAppointmentForm] = useState(false);
  const [appointmentRefreshTrigger, setAppointmentRefreshTrigger] = useState(0);
  const [appointmentsExpanded, setAppointmentsExpanded] = useState(false);
  const [messagesExpanded, setMessagesExpanded] = useState(true);
  const [notesExpanded, setNotesExpanded] = useState(false);
  const [historyExpanded, setHistoryExpanded] = useState(false);

  // State for section counts
  const [appointmentCount, setAppointmentCount] = useState(0);
  const [messageCount, setMessageCount] = useState(0);
  const [noteCount, setNoteCount] = useState(0);

  // Bumped after a send from the act row so the messaging thread refetches.
  const [messageRefreshTrigger, setMessageRefreshTrigger] = useState(0);

  // State for status-triggered appointment scheduling
  const [showScheduleModal, setShowScheduleModal] = useState(false);

  // State for note form
  const [showNoteForm, setShowNoteForm] = useState(false);
  // Remounts LeadNotes so it refetches after a note is saved.
  const [noteRefreshKey, setNoteRefreshKey] = useState(0);

  const effectiveId = propLead?.id || urlId;

  const [isOpen, setIsOpen] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(() => {
    return localStorage.getItem("hasInteractedWithFollowUpDate") === "true";
  });
  const [followUpDismissed, setFollowUpDismissed] = useState(() => {
    return localStorage.getItem("followUpFeatureDismissed") === "true";
  });

  const fetchLead = async () => {
    if (isSideCart && propLead) {
      // For sidecart, always fetch fresh lead data to ensure we have latest estimated_value
      try {
        const [leadData, servicesData] = await Promise.all([
          getLead(authToken, propLead.id),
          getLeadServices(authToken, propLead.id),
        ]);
        setLead(leadData);
        setLeadServices(servicesData || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching lead for sidecart:", err);
        // Fallback to propLead if fetch fails
        setLead(propLead);
        setLeadServices([]);
      }
      setLoading(false);
    } else if (!effectiveId) {
      setError("Invalid lead ID");
      setLoading(false);
    } else {
      try {
        setLoading(true);
        const [leadData, servicesData] = await Promise.all([
          getLead(authToken, effectiveId),
          getLeadServices(authToken, effectiveId),
        ]);
        setLead(leadData);
        setLeadServices(servicesData || []);
        setError(null);
      } catch (err) {
        console.error("Error fetching lead:", err);
        setError("Failed to fetch lead details. Please try again.");
      } finally {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    fetchLead();
  }, [authToken, effectiveId, propLead?.id]);

  // Fire lead_viewed once per lead — fetchLead also runs after every edit
  const trackedLeadId = useRef(null);
  useEffect(() => {
    if (!lead?.id || trackedLeadId.current === lead.id) return;
    trackedLeadId.current = lead.id;
    track("lead_viewed", {
      lead_id: lead.id,
      source: lead.source,
      type: lead.type,
      category: lead.lead_category,
      ai_priority: lead.ai_score,
      was_unread: !lead.is_read_by_user,
      surface: isSideCart ? "sidecart" : "page",
    });
  }, [lead, isSideCart]);

  // Mark lead as read when viewing (only for full page view, not sidecart)
  useEffect(() => {
    const markAsRead = async () => {
      if (effectiveId && !isSideCart) {
        try {
          await markLeadAsRead(effectiveId);
        } catch (error) {
          console.error("Failed to mark lead as read:", error);
        }
      }
    };
    markAsRead();
  }, [effectiveId, isSideCart]);

  // Mark as interacted when the user opens the dropdown
  useEffect(() => {
    if (isOpen && !hasInteracted) {
      setHasInteracted(true);
      localStorage.setItem("hasInteractedWithFollowUpDate", "true");
    }
  }, [isOpen, hasInteracted]);

  const handleServicesChange = async (updatedServices) => {
    setServicesLoading(true);
    try {
      await updateLeadServices(authToken, effectiveId, updatedServices);
      setLeadServices(updatedServices);
      // Fetch updated estimated_value only
      const updatedLead = await getLead(authToken, effectiveId);
      setLead((prev) => ({
        ...prev,
        estimated_value: updatedLead.estimated_value,
      }));
      // Don't trigger full list refresh for service changes in sidecart
      if (onLeadUpdate && !isSideCart) {
        onLeadUpdate(effectiveId);
      }
    } catch (error) {
      console.error("Error updating services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleNewServiceAdded = async (updatedServices, newService) => {
    setServicesLoading(true);
    try {
      await updateLeadServices(authToken, effectiveId, updatedServices);
      setLeadServices(updatedServices);
      // Just update the estimated_value without full lead refresh
      setLead((prev) => ({
        ...prev,
        estimated_value:
          prev.estimated_value + parseFloat(newService.price || 0),
      }));
      // Don't trigger full list refresh for service changes in sidecart
      if (onLeadUpdate && !isSideCart) {
        onLeadUpdate(effectiveId);
      }
    } catch (error) {
      console.error("Error updating services:", error);
      setError("Failed to update services. Please try again.");
    } finally {
      setServicesLoading(false);
    }
  };

  const handleDismissFollowUp = () => {
    setFollowUpDismissed(true);
    localStorage.setItem("followUpFeatureDismissed", "true");
  };

  useEffect(() => {
    const fetchLocations = async () => {
      try {
        const fetchedLocations = await getLocations(authToken);
        setLocations(fetchedLocations);
      } catch (err) {
        console.error("Error fetching locations:", err);
        setError("Failed to fetch locations. You can still edit other fields.");
      }
    };
    fetchLocations();
  }, [authToken]);

  const currentLead = lead || propLead;

  useEffect(() => {
    console.log("Current lead status:", currentLead?.status);
  }, [currentLead]);

  const [updatingStatus, setUpdatingStatus] = useState(false);

  const nameOrPhone = useMemo(() => {
    const fullName = `${currentLead?.first_name || ""} ${
      currentLead?.last_name || ""
    }`.trim();

    if (fullName) {
      return fullName;
    }

    return (
      <span className="text-orange-600 bg-orange-100 px-2 py-1 rounded text-sm font-medium">
        ⚠ Name Required
      </span>
    );
  }, [currentLead]);

  const trackStatusChange = (fromStatus, toStatus) => {
    track("lead_status_changed", {
      lead_id: currentLead.id,
      from_status: fromStatus,
      to_status: toStatus,
      hours_since_created: hoursSinceCreated(currentLead),
    });
    if (toStatus === "converted") {
      track("lead_converted", {
        lead_id: currentLead.id,
        days_to_convert: hoursSinceCreated(currentLead) / 24,
      });
    }
  };

  // A staff text to a waiting lead also moves status new -> contacted on the
  // server; mirror it so the reason line clears without a refetch. Only new
  // leads need a callback, so that move clears it too.
  const handleStaffTextSent = () => {
    if (!currentLead?.waiting_reply) return;
    const status = currentLead.status === "new" ? "contacted" : currentLead.status;
    setLead((prev) => ({ ...prev, waiting_reply: false, status, needs_callback: false }));
  };

  const handleActRowSent = () => {
    handleStaffTextSent();
    setMessageRefreshTrigger((prev) => prev + 1);
  };

  const handleAddNote = () => {
    setShowNoteForm(true);
  };

  const handleNoteCreated = () => {
    setShowNoteForm(false);
    setNotesExpanded(true);
    setNoteRefreshKey((prev) => prev + 1);
  };

  const handleStatusChange = async (newStatus) => {
    if (!currentLead?.id) {
      setError("Cannot update status: No lead ID available.");
      return;
    }

    // Check if the new status requires a loss reason
    if (newStatus === "lost") {
      setPendingStatus(newStatus);
      setIsLossReasonModalOpen(true);
      return;
    }

    // Check if the new status is "contacted" and show modal
    if (newStatus === "contacted") {
      setIsContactedModalOpen(true);
      return;
    }

    // For other statuses, update directly
    try {
      setUpdatingStatus(true);
      await updateLead(authToken, currentLead.id, { status: newStatus });

      trackStatusChange(currentLead.status, newStatus);

      // Update the lead status locally instead of refetching all data.
      // Only new leads need a callback, so a status change clears it.
      setLead((prevLead) => ({
        ...prevLead,
        status: newStatus,
        needs_callback: false,
      }));

      // Don't trigger external refresh in sidecart mode to prevent modal dismissal
      if (onLeadUpdate && !isSideCart) {
        onLeadUpdate(currentLead.id);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      setError("Failed to update lead status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleCategoryChange = async (newCategory) => {
    if (!currentLead?.id) return;

    if (newCategory === "not_applicable") {
      setPendingCategory(newCategory);
      setIsLossReasonModalOpen(true);
      return;
    }

    try {
      setUpdatingStatus(true);
      await updateLead(authToken, currentLead.id, { lead_category: newCategory });
      setLead((prevLead) => ({ ...prevLead, lead_category: newCategory }));
      if (onLeadUpdate && !isSideCart) onLeadUpdate(currentLead.id);
    } catch (err) {
      console.error("Error updating lead category:", err);
      setError("Failed to update lead category. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Staff correction of the AI's next action. Stored in next_action, which wins over
  // ai_next_action — re-analysis keeps updating the AI's own field without clobbering this.
  const handleNextActionChange = async (newAction) => {
    if (!currentLead?.id) return;
    const value = newAction || null;
    try {
      setUpdatingStatus(true);
      await updateLead(authToken, currentLead.id, { next_action: value });
      setLead((prevLead) => ({
        ...prevLead,
        next_action: value,
        effective_next_action: value || prevLead.ai_next_action,
      }));
      if (onLeadUpdate) onLeadUpdate(currentLead.id);
    } catch (err) {
      console.error("Error updating next action:", err);
      setError("Could not save the next action. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleLossReasonSave = async (status, category, lossReason, lossReasonNotes) => {
    try {
      setUpdatingStatus(true);
      const updateData = { loss_reason: lossReason, loss_reason_notes: lossReasonNotes };
      if (category === "not_applicable") {
        updateData.lead_category = "not_applicable";
        updateData.status = "lost";
      } else {
        updateData.status = status;
      }
      await updateLead(authToken, currentLead.id, updateData);
      trackStatusChange(currentLead.status, updateData.status);
      track("lead_lost", {
        lead_id: currentLead.id,
        loss_reason: lossReason,
      });
      await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id);
      }
    } catch (err) {
      console.error("Error saving loss reason:", err);
      setError("Failed to update lead. Please try again.");
      throw err;
    } finally {
      setUpdatingStatus(false);
      setPendingStatus(null);
      setPendingCategory(null);
    }
  };

  const handleLossReasonModalClose = () => {
    setIsLossReasonModalOpen(false);
    setPendingStatus(null);
    setPendingCategory(null);
  };

  const handleContactedModalClose = () => {
    setIsContactedModalOpen(false);
  };

  const handleContactedModalSave = async (status) => {
    try {
      setUpdatingStatus(true);
      await updateLead(authToken, currentLead.id, {
        status: status,
      });
      trackStatusChange(currentLead.status, status);
      await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id);
      }
      setIsContactedModalOpen(false);
    } catch (error) {
      console.error("Failed to update lead status:", error);
      setError("Failed to update lead status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  // Appointment handlers
  const handleAppointmentCreated = async () => {
    setAppointmentRefreshTrigger((prev) => prev + 1);
    setShowAppointmentForm(false);

    // Refresh lead data to get updated status from backend
    await fetchLead();
    if (onLeadUpdate) {
      onLeadUpdate(currentLead.id);
    }
  };

  const handleScheduleModalAppointmentCreated = async () => {
    setAppointmentRefreshTrigger((prev) => prev + 1);
    setShowScheduleModal(false);

    // Refresh lead data to get updated status from backend
    await fetchLead();
    if (onLeadUpdate) {
      onLeadUpdate(currentLead.id);
    }
  };

  const handleScheduleAppointment = () => {
    setShowScheduleModal(true);
  };

  const handleCreateNote = () => {
    setShowNoteForm(true);
  };

  const handleDeleteLead = async () => {
    if (!currentLead?.id) {
      setError("Cannot delete lead: No lead ID available.");
      return;
    }
    if (window.confirm("Are you sure you want to delete this lead?")) {
      try {
        await deleteLead(authToken, currentLead.id);
        navigate("/leads");
      } catch (error) {
        console.error("Error deleting lead:", error);
        setError("Failed to delete lead. Please try again.");
      }
    }
  };

  const startEditing = (field, initialValue) => {
    setEditingField(field);
    setEditValue(initialValue);
  };

  const handleEditChange = (value) => {
    setEditValue(value);
  };

  const cancelEditing = () => {
    setEditingField(null);
    setEditValue("");
  };

  const saveEdit = async (field, valueOverride = null) => {
    try {
      const valueToUse = valueOverride !== null ? valueOverride : editValue;
      let updateData = {};
      if (field === "phone") {
        updateData = { phone: valueToUse };
      } else if (field === "email") {
        updateData = { email: valueToUse };
      } else if (field === "location") {
        // The serializer exposes `location` read-only; `location_id` is the
        // writable field, so a `location` key here is silently dropped.
        updateData = {
          location_id: valueToUse === "" ? null : parseInt(valueToUse, 10),
        };
      } else if (field === "first_name") {
        updateData = { first_name: valueToUse };
      } else if (field === "last_name") {
        updateData = { last_name: valueToUse };
      } else if (field === "name") {
        // Split the full name into first and last name
        const nameParts = valueToUse.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName = nameParts.slice(1).join(" ") || "";
        updateData = {
          first_name: firstName,
          last_name: lastName,
        };
      } else if (field === "source") {
        updateData = { source: valueToUse };
      } else if (field === "insurance_provider") {
        updateData = { insurance_provider: valueToUse };
      } else if (field === "follow_up_date") {
        updateData = {
          follow_up_date:
            valueToUse && valueToUse instanceof Date
              ? valueToUse.toISOString().split("T")[0]
              : null,
        };
      }

      await editLead(authToken, currentLead.id, updateData);
      await fetchLead();
      if (onLeadUpdate) {
        onLeadUpdate(currentLead.id);
      }
      setEditingField(null);
      setEditValue("");
    } catch (error) {
      console.error(`Error updating lead ${field}:`, error);
      setError(`Failed to update ${field}. Please try again.`);
    }
  };

  // Handle note selection
  const handleNoteSelect = (noteId) => {
    setSelectedNoteId(noteId);
  };

  // Handle closing the note view
  const handleCloseNote = () => {
    setSelectedNoteId(null);
  };

  // Handle note update (refresh notes list)
  const handleNoteUpdated = () => {
    fetchLead();
  };

  // Handle note deletion (close note view and refresh)
  const handleNoteDeleted = () => {
    setSelectedNoteId(null);
    fetchLead();
  };

  // Dynamically update the side cart title
  useEffect(() => {
    if (isSideCart && setTitle) {
      if (selectedNoteId) {
        setTitle(<span>Note Details</span>);
      } else if (currentLead) {
        setTitle(
          <div className="flex items-center px-10 space-x-2 whitespace-nowrap">
            <span>{formatLeadTypeToIcon(currentLead.type)}</span>
            <span>{nameOrPhone}</span>
            <span> ({currentLead.request_count || 0})</span>
          </div>
        );
      } else {
        setTitle(<span>Lead Details</span>);
      }
    }
  }, [selectedNoteId, isSideCart, setTitle, currentLead]);

  // If this is a sidecart, use the dedicated sidecart component
  if (isSideCart) {
    return (
      <LeadDetailSidecart
        authToken={authToken}
        lead={propLead}
        onLeadUpdate={onLeadUpdate}
        setTitle={setTitle}
        onClose={onClose}
        onMessagesRead={onMessagesRead}
      />
    );
  }

  if (loading)
    return <div className="text-center py-8">Loading lead details...</div>;
  if (error)
    return <div className="text-center py-8 text-red-600">{error}</div>;
  if (!currentLead)
    return <div className="text-center py-8">No lead found.</div>;

  // The header and the details are two parts of one hero, each drawn once.
  const heroProps = {
    currentLead,
    isSideCart: false,
    nameOrPhone,
    editingField,
    editValue,
    locations,
    leadServices,
    followUpDismissed,
    hasInteracted,
    isOpen,
    setIsOpen,
    startEditing,
    handleEditChange,
    cancelEditing,
    saveEdit,
    handleDismissFollowUp,
    currentCategory: currentLead.lead_category || "prospect",
    onCategoryChange: handleCategoryChange,
  };

  return (
    <div className="w-full bg-background pt-20 md:pt-24 pb-0">
      {selectedNoteId ? (
        <div className="mt-10 bg-white shadow-sm rounded-lg p-6">
          <NoteDetail
            authToken={authToken}
            leadId={effectiveId}
            noteId={selectedNoteId}
            onNoteUpdated={handleNoteUpdated}
            onNoteDeleted={handleNoteDeleted}
            onClose={handleCloseNote}
          />
        </div>
      ) : isMobile ? (
        <LeadMobilePage
          authToken={authToken}
          lead={currentLead}
          nameOrPhone={nameOrPhone}
          onStatusChange={handleStatusChange}
          onLeadUpdate={(updated) => setLead((prev) => ({ ...prev, ...updated }))}
          onSent={handleActRowSent}
          onMessagesRead={onMessagesRead}
          messageRefreshTrigger={messageRefreshTrigger}
        />
      ) : (
        <div className="relative">
          <LeadNavigationButtons
            currentLead={currentLead}
            isSideCart={false}
            onDeleteLead={handleDeleteLead}
          />

          {/* Hidden components to fetch counts for navigation and auto-expand logic */}
          <div className="hidden">
            <LeadAppointmentList
              authToken={authToken}
              leadId={currentLead.id}
              refreshTrigger={appointmentRefreshTrigger}
              compact={true}
              onCountChange={setAppointmentCount}
            />
            <LeadMessaging
              authToken={authToken}
              leadId={currentLead.id}
              lead={currentLead}
              compact={true}
              onCountChange={setMessageCount}
              onMessagesRead={onMessagesRead}
              refreshTrigger={messageRefreshTrigger}
            />
            <LeadNotes
              key={noteRefreshKey}
              authToken={authToken}
              id={currentLead.id}
              onNoteSelect={() => {}}
              onExpandRequest={() => {}}
              onCountChange={setNoteCount}
            />
          </div>

          <div className="mx-auto sm:px-6 lg:px-8">
            <div className="px-4 pt-3 pb-1">
              <Link
                to="/leads"
                className="inline-flex items-center text-primary hover:text-teal-800 font-medium transition-colors text-sm"
              >
                <svg
                  className="h-4 w-4 mr-1"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 19l-7-7 7-7"
                  />
                </svg>
                Leads
              </Link>
            </div>

            {/* Two columns from lg: identity, status and details on the
                left; the flow on the right. Below lg the column wrappers
                drop out (contents) and the order classes stack the parts in
                pane order. */}
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:gap-6">
              <div className="contents lg:flex lg:w-2/5 lg:flex-shrink-0 lg:flex-col lg:gap-4">
                <div id="details" className="order-1">
                  <LeadHeroSection {...heroProps} part="header" />
                </div>
                <div className="order-3">
                  <LeadStatusControl
                    lead={currentLead}
                    onStatusChange={handleStatusChange}
                    onCategoryChange={handleCategoryChange}
                    onScheduleAppointment={handleScheduleAppointment}
                    onNextActionChange={handleNextActionChange}
                    disabled={updatingStatus}
                  />
                </div>
                <div className="order-4">
                  <LeadHeroSection {...heroProps} part="details" />
                </div>
              </div>
              <div className="contents lg:flex lg:min-w-0 lg:flex-1 lg:flex-col lg:gap-4">
                <div className="order-2 bg-white p-4 shadow-sm rounded-lg hover:border hover:border-primary/40 transition duration-300">
                  <ReasonLine
                    lead={currentLead}
                    onLeadUpdate={(updated) => setLead((prev) => ({ ...prev, ...updated }))}
                    className="mb-4"
                  />

                  <div className="mb-4">
                    <AboutLead lead={currentLead} />
                  </div>

                  <LeadActRow
                    authToken={authToken}
                    lead={currentLead}
                    onSent={handleActRowSent}
                    onBook={handleScheduleAppointment}
                    onAddNote={handleAddNote}
                  />

                  {/* Inline Editing Feature Callout */}
                  {false && (
                    <div className="bg-gradient-to-r from-purple-50 to-indigo-50 border border-purple-200 rounded-lg p-3 mb-4 flex items-center space-x-3">
                      <div className="flex-shrink-0">
                        <svg
                          className="h-5 w-5 text-purple-600"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                      </div>
                      <div className="flex-1">
                        <p className="text-sm text-purple-800 font-medium flex items-center space-x-2">
                          <span>
                            💡 <strong>New Feature:</strong> Click any
                          </span>
                          <svg
                            className="h-4 w-4 bg-accent-teal-subtle text-slate-600 p-0.5 rounded-full border border-teal-200"
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                            />
                          </svg>
                          <span>
                            icon below to modify lead details instantly - no
                            need to navigate away!
                          </span>
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Services Management Section - Prominent Placement */}
                  {false && (
                    <div className="bg-white border border-green-200 rounded-lg p-4 mb-4">
                      <div className="flex items-center mb-3">
                        <svg
                          className="h-5 w-5 text-green-600 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M9 5H7a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2V7a2 2 0 00-2-2h-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                          />
                        </svg>
                        <h3 className="text-lg font-semibold text-green-800">
                          Services of Interest
                        </h3>
                        <span className="ml-3 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                          Manage Services
                        </span>
                      </div>
                      <ServiceSelector
                        selectedServices={leadServices}
                        onServicesChange={handleServicesChange}
                        authToken={authToken}
                        leadId={effectiveId}
                        lead={currentLead}
                        onNewServiceAdded={handleNewServiceAdded}
                      />
                      {servicesLoading && (
                        <div className="mt-2 text-sm text-gray-600">
                          Updating services...
                        </div>
                      )}
                    </div>
                  )}
                </div>

                <div className="order-5">
                  {/* Messages Section */}
                  <div
                    id="messages"
                    className="mt-0 mb-4 bg-white border border-border  rounded-lg hover:border hover:border-primary/40 transition duration-300 w-full scroll-mt-32"
                  >
                    <div className="px-4 py-3 border-b border-border ">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            onClick={() =>
                              setMessagesExpanded(!messagesExpanded)
                            }
                            className="flex items-center mr-3 text-gray-600 hover:text-gray-800 focus:outline-none focus:text-gray-800 transition-colors"
                          >
                            <svg
                              className={`h-5 w-5 transform transition-transform duration-200 ${
                                messagesExpanded ? "rotate-90" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                            <svg
                              className="h-5 w-5 mr-2 text-gray-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
                              />
                            </svg>
                            Messages
                            {messageCount > 0 && (
                              <span className="ml-2 px-2 py-0.5 bg-accent-teal-subtle text-teal-800 text-xs font-medium rounded-full">
                                {messageCount}
                              </span>
                            )}
                          </h3>
                        </div>
                      </div>
                      <p className="ml-8 mt-1 text-sm text-gray-600">
                        View SMS messages. Write in the box above.
                      </p>
                    </div>

                    {messagesExpanded && (
                      <div className="p-4">
                        <LeadMessaging
                          authToken={authToken}
                          leadId={currentLead.id}
                          lead={currentLead}
                          compact={false}
                          onCountChange={setMessageCount}
                          onMessagesRead={onMessagesRead}
                          refreshTrigger={messageRefreshTrigger}
                        />
                      </div>
                    )}
                  </div>

                  {/* Appointments Section */}
                  <div
                    id="appointments"
                    className="mb-4 bg-white shadow-sm border border-border  hover:border-primary/40 rounded-lg scroll-mt-32"
                  >
                    <div className="px-4 py-2 border-b border-border ">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            onClick={() =>
                              setAppointmentsExpanded(!appointmentsExpanded)
                            }
                            className="flex items-center mr-3 text-gray-600 hover:text-gray-800 focus:outline-none focus:text-gray-800 transition-colors"
                          >
                            <svg
                              className={`h-5 w-5 transform transition-transform duration-200 ${
                                appointmentsExpanded ? "rotate-90" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                          <h3 className="text-lg font-semibold text-gray-800 flex items-center">
                            <svg
                              className="h-5 w-5 mr-2 text-gray-600"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                            Appointments
                            {appointmentCount > 0 && (
                              <span className="ml-2 px-2 py-0.5 bg-accent-teal-subtle text-teal-800 text-xs font-medium rounded-full">
                                {appointmentCount}
                              </span>
                            )}
                            <HelpTooltip
                              title={appointmentHelp.appointmentsOverview.title}
                              content={
                                appointmentHelp.appointmentsOverview.content
                              }
                              className="ml-2"
                              size="lg"
                              position="top-right"
                            />
                          </h3>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => {
                              if (!appointmentsExpanded) {
                                setAppointmentsExpanded(true);
                              }
                              setShowAppointmentForm(!showAppointmentForm);
                            }}
                            className={`inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg focus:ring-2 focus:ring-offset-2 transition-colors ${
                              showAppointmentForm
                                ? "text-gray-600 bg-background hover:bg-background/110-300 focus:ring-gray-400"
                                : "text-white bg-secondary hover:bg-teal-800 focus:ring-teal-600"
                            }`}
                          >
                            <svg
                              className="h-4 w-4 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d={
                                  showAppointmentForm
                                    ? "M6 18L18 6M6 6l12 12"
                                    : "M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                }
                              />
                            </svg>
                            {showAppointmentForm
                              ? "Cancel Appointment"
                              : "New Appointment"}
                          </button>
                        </div>
                      </div>
                      <p className="ml-8 mt-1 text-sm text-gray-600">
                        Schedule appointments and send reminders
                      </p>
                    </div>

                    {appointmentsExpanded && (
                      <div className="p-4">
                        {showAppointmentForm && (
                          <div className="mb-6">
                            <AppointmentForm
                              authToken={authToken}
                              leadId={currentLead.id}
                              onAppointmentCreated={handleAppointmentCreated}
                              onClose={() => setShowAppointmentForm(false)}
                              compact={false}
                            />
                          </div>
                        )}

                        <LeadAppointmentList
                          authToken={authToken}
                          leadId={currentLead.id}
                          refreshTrigger={appointmentRefreshTrigger}
                          compact={false}
                          onCountChange={setAppointmentCount}
                        />
                      </div>
                    )}
                  </div>

                  {/* Lead Notes Section - Prominent Placement */}
                  <div
                    id="notes"
                    className="bg-white border border-border  hover:border-primary/40 rounded-lg mb-6 scroll-mt-32"
                  >
                    <div className="px-4 py-3 border-b border-blue-100">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center">
                          <button
                            onClick={() => setNotesExpanded(!notesExpanded)}
                            className="flex items-center mr-3 text-grey-600 hover:text-blue-800 focus:outline-none focus:text-blue-800 transition-colors"
                          >
                            <svg
                              className={`h-5 w-5 transform transition-transform duration-200 ${
                                notesExpanded ? "rotate-90" : ""
                              }`}
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 5l7 7-7 7"
                              />
                            </svg>
                          </button>
                          <div className="flex items-center">
                            <svg
                              className="h-5 w-5 text-blue-600 mr-2"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            <h3 className="text-sm font-semibold text-gray-800 flex items-center">
                              Lead Notes & Communication
                              {noteCount > 0 && (
                                <span className="ml-2 px-2 py-0.5 bg-green-100 text-green-800 text-xs font-medium rounded-full">
                                  {noteCount}
                                </span>
                              )}
                            </h3>
                          </div>
                        </div>
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={handleCreateNote}
                            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-lg focus:ring-2 focus:ring-offset-2 transition-colors text-white bg-teal-700 hover:bg-teal-800 focus:ring-teal-600"
                          >
                            <svg
                              className="h-4 w-4 mr-1"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                              />
                            </svg>
                            New Note
                          </button>
                        </div>
                      </div>
                      <p className="ml-8 mt-1 text-sm text-gray-600">
                        Add notes and track communication history
                      </p>
                    </div>

                    {notesExpanded && (
                      <div className="p-2 relative z-10">
                        <LeadNotes
                          key={noteRefreshKey}
                          authToken={authToken}
                          id={currentLead.id}
                          onNoteSelect={handleNoteSelect}
                          onExpandRequest={() => setNotesExpanded(true)}
                          onCountChange={setNoteCount}
                        />
                      </div>
                    )}
                  </div>

                  {currentLead.address && (
                    <div className="border-t border-border  px-4 py-5 sm:px-6">
                      <h3 className="text-sm font-semibold text-gray-800 mb-3">
                        Address
                      </h3>
                      <address className="text-sm text-gray-600 not-italic">
                        <p>{currentLead.address.street}</p>
                        <p>
                          {currentLead.address.city},{" "}
                          {currentLead.address.state}{" "}
                          {currentLead.address.postal_code}
                        </p>
                        <p>{currentLead.address.country}</p>
                      </address>
                    </div>
                  )}

                  {/* History Section */}
                  <div className="bg-white border border-border rounded-lg hover:border-primary/40 transition duration-300 w-full">
                    <div className="px-4 py-3">
                      <div className="flex items-center">
                        <button
                          onClick={() => setHistoryExpanded(!historyExpanded)}
                          className="flex items-center mr-3 text-gray-600 hover:text-gray-800 focus:outline-none focus:text-gray-800 transition-colors"
                        >
                          <svg
                            className={`h-5 w-5 transform transition-transform duration-200 ${
                              historyExpanded ? "rotate-90" : ""
                            }`}
                            fill="none"
                            viewBox="0 0 24 24"
                            stroke="currentColor"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M9 5l7 7-7 7"
                            />
                          </svg>
                        </button>
                        <h3 className="text-lg font-semibold text-gray-800">
                          History
                        </h3>
                      </div>
                    </div>

                    {historyExpanded && (
                      <div className="border-t border-border w-full">
                        <ObjectHistory
                          authToken={authToken}
                          objectType="lead"
                          objectId={currentLead.id}
                        />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loss Reason Modal */}
      <LossReasonModal
        isOpen={isLossReasonModalOpen}
        onClose={handleLossReasonModalClose}
        onSave={handleLossReasonSave}
        status={pendingStatus}
        category={pendingCategory}
        initialReason={currentLead?.loss_reason || ""}
        initialNotes={currentLead?.loss_reason_notes || ""}
      />

      {/* Contacted Modal */}
      <ContactedModal
        isOpen={isContactedModalOpen}
        onClose={handleContactedModalClose}
        onSave={handleContactedModalSave}
        authToken={authToken}
        leadId={currentLead?.id}
        currentFollowUpDate={currentLead?.follow_up_date}
        onLeadUpdate={(updateData) => {
          setLead((prevLead) => ({
            ...prevLead,
            ...updateData,
          }));
        }}
      />

      {/* Schedule Appointment Modal */}
      {showScheduleModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-teal-800">
                    Schedule Appointment
                  </h3>
                </div>
                <button
                  onClick={() => setShowScheduleModal(false)}
                  className="text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full p-1 transition duration-300"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <p className="text-gray-600 mb-6">
                Schedule an appointment for this lead. Once created, the lead
                status will be updated to "Scheduled".
              </p>

              <AppointmentForm
                authToken={authToken}
                leadId={currentLead.id}
                onAppointmentCreated={handleScheduleModalAppointmentCreated}
                onClose={() => setShowScheduleModal(false)}
                compact={false}
              />
            </div>
          </div>
        </div>
      )}
      {/* Add Note Modal */}
      {showNoteForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000]">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center">
                  <svg
                    className="h-6 w-6 text-primary mr-3"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  <h3 className="text-lg font-semibold text-teal-800">
                    Add Note
                  </h3>
                </div>
                <button
                  onClick={() => setShowNoteForm(false)}
                  className="text-gray-400 hover:text-gray-600 focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 rounded-full p-1 transition duration-300"
                >
                  <svg
                    className="h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M6 18L18 6M6 6l12 12"
                    />
                  </svg>
                </button>
              </div>

              <NoteCreate
                authToken={authToken}
                contentType="leads"
                patientId={currentLead.id}
                onNoteCreated={handleNoteCreated}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadDetail;